import api from '../axiosConfig';
import { AUTH } from '../config/apiEndpoints';
import { getCookie, deleteCookie } from '../../utils/cookies';

export const LoginService = {
    login: async (email, password) => {
        try {
            const response = await api.post(AUTH.LOGIN, { email, password });
            return response.data;
        } catch (error) {
            console.error('Login service error:', error);
            throw error;
        }
    },

    verifyMfa: async (tempToken, mfaCode, rememberDevice) => {
        try {
            const response = await api.post(AUTH.MFA_VERIFY, { tempToken, mfaCode, rememberDevice });
            return response.data;
        } catch (error) {
            console.error('Verify MFA service error:', error);
            throw error;
        }
    },

    setupMfa: async (tenantId, method = 'TOTP') => {
        try {
            const response = await api.get(AUTH.MFA_SETUP, { 
                params: { method },
                headers: { 'x-tenant-id': tenantId } 
            });
            return response.data;
        } catch (error) {
            console.error('Setup MFA service error:', error);
            throw error;
        }
    },

    toggleMfa: async (enabled, method, mfaCode, secret) => {
        try {
            const response = await api.put(AUTH.MFA_TOGGLE, { enabled, method, mfaCode, secret });
            return response.data;
        } catch (error) {
            console.error('Toggle MFA service error:', error);
            throw error;
        }
    },

    logout: async () => {
        try {
            const refreshToken = getCookie('adminRefreshToken');
            await api.post(AUTH.LOGOUT, { refreshToken });
        } catch (error) {
            console.error('Logout API error:', error);
        } finally {
            deleteCookie('adminToken');
            deleteCookie('adminRefreshToken');
            deleteCookie('adminAuth');
            deleteCookie('adminGlobalToken');
            deleteCookie('adminGlobalRefreshToken');
        }
    },

    refresh: async () => {
        const refreshToken = getCookie('adminRefreshToken');
        if (!refreshToken) throw new Error('No refresh token available');
        const response = await api.post(AUTH.REFRESH, { refreshToken });
        return response.data;
    },

    forgotPassword: async (email) => {
        try {
            const response = await api.post(AUTH.FORGOT_PASSWORD, { email });
            return response.data;
        } catch (error) {
            console.error('ForgotPassword service error:', error);
            throw error;
        }
    },

    verifyOtp: async (email, otp) => {
        try {
            const response = await api.post(AUTH.VERIFY_OTP, { email, otp });
            return response.data;
        } catch (error) {
            console.error('VerifyOtp service error:', error);
            throw error;
        }
    },

    resetPassword: async (token, password) => {
        try {
            const response = await api.post(AUTH.RESET_PASSWORD, { token, password });
            return response.data;
        } catch (error) {
            console.error('ResetPassword service error:', error);
            throw error;
        }
    },

    getCurrentUser: async () => {
        try {
            const response = await api.get(AUTH.ME);
            const data = response.data;
            return data.data || data;
        } catch (error) {
            console.error('GetCurrentUser service error:', error);
            throw error;
        }
    },

    changePassword: async (oldPassword, newPassword) => {
        try {
            const response = await api.put(AUTH.CHANGE_PASSWORD, { oldPassword, newPassword });
            return response.data;
        } catch (error) {
            throw (
                error.response?.data?.message ||
                error.message ||
                'Failed to change password'
            );
        }
    },

    // ─── Session Management ───────────────────────────────────────────────────

    extendSession: async () => {
        try {
            const response = await api.post(AUTH.SESSION_EXTEND);
            return response.data;
        } catch (error) {
            console.error('Extend session error:', error);
            throw error;
        }
    },

    getActiveSessions: async ({ page = 0, limit = 20 } = {}) => {
        try {
            const response = await api.get(AUTH.SESSIONS, {
                params: { page: page + 1, limit }
            });
            return response.data;
        } catch (error) {
            console.error('Get active sessions error:', error);
            throw error;
        }
    },

    exportActiveSessions: async () => {
        try {
            const response = await api.get(AUTH.SESSIONS, {
                params: { format: 'csv' },
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            console.error('Export active sessions error:', error);
            throw error;
        }
    },

    terminateSession: async (sessionId) => {
        try {
            const response = await api.delete(AUTH.SESSION_BY_ID(sessionId));
            return response.data;
        } catch (error) {
            console.error('Terminate session error:', error);
            throw error;
        }
    },

    // ─── Audit Logs ───────────────────────────────────────────────────────────

    getAuditLogs: async ({ page = 0, limit = 20, search, dateFrom, dateTo, user, action } = {}) => {
        try {
            const params = { page: page + 1, limit };
            if (search) params.search = search;
            if (dateFrom) params.startDate = dateFrom;
            if (dateTo) params.endDate = dateTo;
            if (user) params.user = user;
            if (action && action !== 'All') params.action = action;
            const response = await api.get(AUTH.AUDIT_LOGS, { params });
            return response.data;
        } catch (error) {
            console.error('Get audit logs error:', error);
            throw error;
        }
    },

    exportAuditLogs: async ({ search, dateFrom, dateTo, user, action, format = 'csv' } = {}) => {
        try {
            const params = { format };
            if (search) params.search = search;
            if (dateFrom) params.startDate = dateFrom;
            if (dateTo) params.endDate = dateTo;
            if (user) params.userId = user;
            if (action && action !== 'All') params.action = action;
            const response = await api.get(AUTH.AUDIT_LOGS_EXPORT, {
                params,
                responseType: 'blob',
            });
            return response;
        } catch (error) {
            console.error('Export audit logs error:', error);
            throw error;
        }
    },

    // ─── Tenant Management ────────────────────────────────────────────────────

    getTenants: async () => {
        try {
            const response = await api.get(AUTH.TENANTS);
            return response.data;
        } catch (error) {
            console.error('Get tenants error:', error);
            throw error;
        }
    },

    switchTenant: async (tenantId) => {
        try {
            const response = await api.post(AUTH.TENANT_SWITCH, { tenantId });
            return response.data;
        } catch (error) {
            console.error('Switch tenant error:', error);
            throw error;
        }
    },
};
